import { useState } from 'react';
import { Loader2, X, Check, Upload } from 'lucide-react';
import type { BankAccount, BankAccountFormData } from '../types';

interface Props {
  existing?: BankAccount | null;
  onSave: (form: BankAccountFormData) => Promise<void>;
  onClose: () => void;
}

const EMPTY: BankAccountFormData = {
  bank_name: '',
  account_name: '',
  account_number: '',
  account_type: '',
  currency: 'BOB',
  is_active: true,
  qr_code_file: null,
};

export function BankAccountForm({ existing, onSave, onClose }: Props) {
  const [form, setForm] = useState<BankAccountFormData>(
    existing
      ? {
          bank_name:      existing.bank_name,
          account_name:   existing.account_name,
          account_number: existing.account_number,
          account_type:   existing.account_type ?? '',
          currency:       existing.currency,
          is_active:      existing.is_active,
          qr_code_file:   null,
        }
      : EMPTY,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [qrPreview, setQrPreview] = useState<string | null>(existing?.qr_code_url ?? null);

  const set = (field: keyof BankAccountFormData, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleQrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    set('qr_code_file', file);
    if (file) setQrPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!form.bank_name.trim() || !form.account_name.trim() || !form.account_number.trim()) {
      setError('Banco, nombre de cuenta y número son obligatorios.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
      onClose();
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {existing ? 'Editar cuenta bancaria' : 'Nueva cuenta bancaria'}
          </h2>
          <button onClick={onClose} disabled={saving} className="text-muted-foreground hover:text-foreground">
            <X className="size-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium">Banco</label>
            <input
              value={form.bank_name}
              onChange={(e) => set('bank_name', e.target.value)}
              disabled={saving}
              placeholder="ej. Banco Mercantil Santa Cruz"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Nombre de la cuenta</label>
            <input
              value={form.account_name}
              onChange={(e) => set('account_name', e.target.value)}
              disabled={saving}
              placeholder="ej. Cuenta Nacional Diezmos"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">N° de cuenta</label>
              <input
                value={form.account_number}
                onChange={(e) => set('account_number', e.target.value)}
                disabled={saving}
                placeholder="0000000000"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Moneda</label>
              <select
                value={form.currency}
                onChange={(e) => set('currency', e.target.value)}
                disabled={saving}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="BOB">Bolivianos (Bs.)</option>
                <option value="USD">Dólares (USD)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">
              Tipo de cuenta <span className="font-normal text-muted-foreground">(opcional)</span>
            </label>
            <input
              value={form.account_type}
              onChange={(e) => set('account_type', e.target.value)}
              disabled={saving}
              placeholder="ej. Caja de Ahorro"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">
              Código QR <span className="font-normal text-muted-foreground">(opcional)</span>
            </label>
            <div className="flex items-start gap-3">
              {qrPreview && (
                <img src={qrPreview} alt="QR" className="size-20 rounded border object-contain" />
              )}
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary">
                <Upload className="size-4" />
                {qrPreview ? 'Cambiar QR' : 'Subir QR'}
                <input type="file" accept="image/*" className="hidden" onChange={handleQrChange} disabled={saving} />
              </label>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
              disabled={saving}
              className="rounded"
            />
            Cuenta activa
          </label>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
